import {
    Button as AriaButton,
    Dialog as AriaDialog,
    DialogTrigger as AriaDialogTrigger,
    Popover as AriaPopover,
} from "react-aria-components";
import { AlertCircle, ArrowRight } from "@untitledui/icons";
import { Button, cx } from "@tricura/atlas-ui";

interface FacilityFlagPopoverProps {
    /** Who first submitted this facility — rendered bold inside the
     *  popover body. Omitted when unknown: the location payload carries
     *  only `duplicate_submission_id`, not the submitter, so the body
     *  falls back to the attribution-free sentence rather than inventing
     *  a name (OR-1442). */
    submittedBy?: string;
    /** When it was previously submitted (US date format MM/DD/YYYY).
     *  Omitted when unknown — see `submittedBy`. */
    submittedDate?: string;
    /** Called when the CTA inside the popover is clicked. Typically
     *  navigates to the original submission detail. */
    onViewOriginal?: () => void;
    /** Extra classes for the trigger — used to place the flag inside the
     *  Facility Name field. */
    className?: string;
}

/**
 * "Previously submitted" flag popover used inline in editable
 * facility tables (e.g. UW Information → Schedule of Location).
 *
 * The trigger is a small pill button hosting the warning glyph
 * (`AlertCircle` in atlas yellow-500) + a `ChevronDown` affordance.
 * Click it to reveal a 320 px card with a sentence about the prior
 * submission and a primary CTA back to the original submission.
 *
 * Built on react-aria-components `DialogTrigger` + `Popover` + `Dialog`
 * — same primitives the atlas-ui internal nav uses, so focus
 * management + outside-click dismiss come for free.
 */
export function FacilityFlagPopover({ submittedBy, submittedDate, onViewOriginal, className }: FacilityFlagPopoverProps) {
    return (
        <AriaDialogTrigger>
            <AriaButton
                aria-label="Duplicate submission details"
                // Compact alert glyph that lives INSIDE the Facility Name
                // field (Andressa review) — the chevron was dropped so it
                // no longer reads as a separate dropdown. `aria-expanded:`
                // keeps the ring visible while the popover is open (focus
                // moves into the dialog).
                className={cx(
                    "hover:bg-secondary_hover focus-visible:ring-focus-ring aria-expanded:ring-focus-ring inline-flex shrink-0 cursor-pointer items-center justify-center rounded-md p-0.5 outline-none transition duration-100 ease-linear focus-visible:ring-2 aria-expanded:ring-2",
                    className,
                )}
            >
                <AlertCircle aria-hidden="true" className="text-utility-yellow-500 size-4" />
            </AriaButton>
            <AriaPopover
                placement="bottom start"
                offset={6}
                className="z-50 entering:animate-in entering:fade-in entering:zoom-in-95"
            >
                <AriaDialog className="bg-primary border-secondary w-[382px] min-w-[256px] rounded-lg border p-4 shadow-lg outline-none">
                    <p className="text-secondary text-sm">
                        {submittedBy && submittedDate ? (
                            <>
                                Previously submitted by{" "}
                                <span className="text-primary font-semibold">{submittedBy}</span> on{" "}
                                <span className="text-primary font-semibold">{submittedDate}</span>.
                            </>
                        ) : (
                            "This facility was previously submitted."
                        )}
                    </p>
                    {/* Full-bleed divider — `-mx-4` cancels the
                      * parent's `p-4` so the line spans the popover
                      * edges instead of sitting indented inside the
                      * content gutter. */}
                    <hr aria-hidden="true" className="border-secondary -mx-4 my-4" />
                    <Button color="primary" size="sm" iconTrailing={ArrowRight} onClick={onViewOriginal}>
                        View Original Submission
                    </Button>
                </AriaDialog>
            </AriaPopover>
        </AriaDialogTrigger>
    );
}
